/** leads/LeadFpPortalPage.tsx — 리드 포털 (FP·WM 열람·정보 수정, 사용자 확정 2026-07-17)
 *  수정은 원본 테이블(fps / wms) 반영 → TM 관리·배정·포털 전 모듈 공통.
 *  2026-09-10: ① 그룹핑(소속 / 기수 / 구분 / 없음 — 접기·펼치기, 그룹별 인원·보유/정원·수락 대기 합계) ② 「링크 보내기」 = 문자 내용 미리보기(수신번호·문안·바이트) 확인 후 발송
 *  ③ 수락 대기 건수 표시 (배정 → 담당자 포털 「수락하기」 → 수락 시 관리 DB)
 *  ④ 「보유 DB」 — 담당자가 가진 DB 목록을 펼쳐 건별·선택·전체 회수 (사용자 지시 2026-09-10: 관리자가 준 DB를 회수할 수 있어야 함) */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { sendLinkPreview, sendLink, listFpHeldLeads, unassignBulk } from './api';
import type { SendLinkPreview, HeldLeads, HeldLead } from './types';
import { STATUS_LABEL } from './types';

interface Row { id: string; fp_id?: string | null; name: string; phone: string; company: string; kind: string; cart_size: number; active_count: number; pending_count?: number; portal_url: string; cohort?: string; stats?: { assigned: number; won: number; lost: number; returned: number } }
const H = () => ({ Authorization: 'Bearer ' + (localStorage.getItem('token') || ''), 'Content-Type': 'application/json' });
type GroupKey = 'none' | 'company' | 'cohort' | 'kind';
const GROUP_KEY = 'ld_fpportal_group';

export default function LeadFpPortalPage({ onToast }: { onToast: (m: string, t?: string) => void }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [q, setQ] = useState('');
  const [kindF, setKindF] = useState<'all' | 'fp' | 'wm'>('all');
  const [cohortF, setCohortF] = useState(''); // 기수 필터 (2026-07-18)
  const [groupBy, setGroupBy] = useState<GroupKey>(() => ((localStorage.getItem(GROUP_KEY) as GroupKey) || 'company'));
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<string | null>(null);
  const [eName, setEName] = useState(''); const [eCo, setECo] = useState(''); const [ePhone, setEPhone] = useState(''); const [eCart, setECart] = useState('10'); const [eCohort, setECohort] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{ row: Row; data: SendLinkPreview; message: string; phone: string } | null>(null); // 링크 보내기 미리보기
  // FP 신규 등록 (2026-09-05) — 리드 포털 화면에서 직접. 연락처 = 고유키(중복이면 서버 409)
  const [addOpen, setAddOpen] = useState(false);
  const [nName, setNName] = useState(''); const [nCo, setNCo] = useState('한화생명금융서비스'); const [nPhone, setNPhone] = useState(''); const [nCart, setNCart] = useState('10'); const [nCohort, setNCohort] = useState('');
  const addFp = async () => {
    if (!nName.trim()) { onToast('이름을 입력하세요', 'error'); return; }
    if (nPhone.replace(/[^0-9]/g, '').length < 10) { onToast('연락처를 숫자 10~11자리로 입력하세요', 'error'); return; }
    setBusy(true);
    try {
      const res = await fetch('/api/leads/fp-portals', { method: 'POST', headers: H(), body: JSON.stringify({ name: nName, company: nCo, phone: nPhone, cart_size: parseInt(nCart, 10) || 10, cohort: nCohort }) });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || '등록 실패');
      setRows((prev) => [j.data as Row, ...prev]);
      setAddOpen(false); setNName(''); setNPhone(''); setNCohort(''); setNCart('10');
      setQ(j.data.name); setKindF('fp');
      onToast(j.data.name + ' FP 등록 완료 — [링크 보내기]로 포털 주소를 전달하세요');
    } catch (e) { onToast((e as Error).message, 'error'); } finally { setBusy(false); }
  };

  const loadRows = useCallback(() => {
    return fetch('/api/leads/fp-portals', { headers: H() })
      .then((r) => r.json())
      .then((j) => { if (j.success) setRows(j.data.rows); else onToast(j.error || '목록 조회 실패', 'error'); })
      .catch(() => onToast('목록 조회 실패', 'error'))
      .finally(() => setLoading(false));
  }, [onToast]);
  useEffect(() => { loadRows(); }, [loadRows]);

  /* ── 보유 DB · 회수 (2026-09-10) ── */
  const [heldFor, setHeldFor] = useState<string | null>(null); // 펼친 행 id
  const [held, setHeld] = useState<HeldLeads | null>(null);
  const [heldLoading, setHeldLoading] = useState(false);
  const [heldSel, setHeldSel] = useState<Set<string>>(new Set());
  const loadHeld = useCallback(async (rowId: string) => {
    setHeldLoading(true); setHeldSel(new Set());
    try { setHeld(await listFpHeldLeads(rowId)); }
    catch (e) { onToast((e as Error).message, 'error'); setHeld(null); }
    finally { setHeldLoading(false); }
  }, [onToast]);
  const toggleHeld = (r: Row) => {
    if (heldFor === r.id) { setHeldFor(null); setHeld(null); return; }
    setHeldFor(r.id); setHeld(null); loadHeld(r.id);
  };
  const doRecall = async (r: Row, ids: string[], label: string) => {
    if (!ids.length) return;
    if (!window.confirm(r.name + ' ' + (r.kind === 'wm' ? 'WM' : 'FP') + '에게 준 DB ' + ids.length + '건을 회수할까요?\n회수한 DB는 미배정으로 돌아가고 담당자 포털에서 사라집니다. (활동 이력은 유지)')) return;
    setBusy(true);
    try {
      const res = await unassignBulk(ids);
      onToast(r.name + ' ' + label + ' 회수 완료 — ' + res.unassigned + '건' + (res.skipped ? ' (이미 회수됨 ' + res.skipped + '건 제외)' : ''));
      await Promise.all([loadRows(), loadHeld(r.id)]);
    } catch (e) { onToast((e as Error).message, 'error'); }
    finally { setBusy(false); }
  };
  const fmtWon = (won: number) => { if (!won) return '-'; const eok = won / 1e8; return eok >= 1 ? (Math.round(eok * 10) / 10) + '억' : Math.round(won / 1e4).toLocaleString() + '만'; };
  const renderHeldPanel = (r: Row) => {
    const list = held?.rows || [];
    const allSel = list.length > 0 && list.every((l) => heldSel.has(l.id));
    const pend = list.filter((l) => !l.accepted).length;
    return (
      <tr key={r.id + ':held'} className="ld-held-row">
        <td colSpan={6} style={{ padding: 0, background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
          <div className="ld-held">
            <div className="ld-held-h">
              <b>{r.name} {r.kind === 'wm' ? 'WM' : 'FP'} 보유 DB</b>
              <span className="mut">{heldLoading ? '불러오는 중…' : list.length + '건' + (pend ? ' · 수락 대기 ' + pend : '')}</span>
              <span style={{ flex: 1 }} />
              <button className="ce-btn sub" style={{ padding: '5px 11px', fontSize: 11.5 }} disabled={busy || heldSel.size === 0} onClick={() => doRecall(r, Array.from(heldSel), '선택 ' + heldSel.size + '건')}>선택 회수{heldSel.size ? ' (' + heldSel.size + ')' : ''}</button>
              <button className="ce-btn" style={{ padding: '5px 11px', fontSize: 11.5, background: '#DC2626', borderColor: '#DC2626' }} disabled={busy || list.length === 0} onClick={() => doRecall(r, list.map((l) => l.id), '전체 ' + list.length + '건')}>전체 회수</button>
              <button className="ce-btn sub" style={{ padding: '5px 9px', fontSize: 11.5 }} onClick={() => toggleHeld(r)} title="접기">✕</button>
            </div>
            {!heldLoading && list.length === 0 && <div className="ce-none" style={{ padding: '14px 12px' }}>보유 중인 DB가 없습니다</div>}
            {list.length > 0 && (
              <table className="ce-tbl ld-held-tbl">
                <thead><tr>
                  <th style={{ width: 34 }}><input type="checkbox" checked={allSel} onChange={() => setHeldSel(allSel ? new Set() : new Set(list.map((l) => l.id)))} title="전체 선택" /></th>
                  <th>회사명</th><th>대표자</th><th>투자연도 · 금액</th><th>상태</th><th>배정일</th><th>수락</th><th></th>
                </tr></thead>
                <tbody>
                  {list.map((l: HeldLead) => (
                    <tr key={l.id} className={l.accepted ? '' : 'pending'}>
                      <td><input type="checkbox" checked={heldSel.has(l.id)} onChange={() => setHeldSel((s0) => { const n = new Set(s0); if (n.has(l.id)) n.delete(l.id); else n.add(l.id); return n; })} /></td>
                      <td><b>{l.client_name}</b>{l.region ? <span className="mut" style={{ marginLeft: 6, fontSize: 11 }}>{l.region}</span> : null}</td>
                      <td className="mut">{l.ceo_name || '-'}</td>
                      <td style={{ fontVariantNumeric: 'tabular-nums' }}>{l.invest_year || '-'}{l.invest_amount ? <span className="mut"> · {fmtWon(l.invest_amount)}</span> : null}</td>
                      <td>{STATUS_LABEL[l.status] || l.status}</td>
                      <td className="mut" style={{ fontVariantNumeric: 'tabular-nums' }}>{l.assigned_at || '-'}</td>
                      <td>{l.accepted
                        ? <span style={{ fontSize: 10.5, fontWeight: 700, color: '#065F46', background: '#D1FAE5', padding: '1px 7px', borderRadius: 99 }} title={'수락일 ' + (l.accepted_at || '')}>수락함</span>
                        : <span style={{ fontSize: 10.5, fontWeight: 700, color: '#92400E', background: '#FEF3C7', padding: '1px 7px', borderRadius: 99 }} title="아직 포털에서 수락하지 않음">수락 대기</span>}</td>
                      <td style={{ whiteSpace: 'nowrap' }}><button className="ce-btn sub" style={{ padding: '3px 9px', fontSize: 10.5 }} disabled={busy} title="이 건만 회수 — 미배정 복귀" onClick={() => doRecall(r, [l.id], l.client_name)}>회수</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="mut" style={{ fontSize: 11, padding: '6px 12px 10px' }}>회수하면 미배정으로 돌아가 다른 담당자에게 다시 배정할 수 있습니다. 담당자 포털에서는 즉시 사라지며, 미팅 결과·메모 등 활동 이력은 그대로 남습니다.</div>
          </div>
        </td>
      </tr>
    );
  };

  const t = q.trim();
  const shown = rows
    .filter((r) => kindF === 'all' || (r.kind === 'wm' ? kindF === 'wm' : kindF === 'fp'))
    .filter((r) => !t || r.name.includes(t) || (r.company || '').includes(t) || (r.phone || '').includes(t))
    .filter((r) => !cohortF || (r.cohort || '') === cohortF);
  const cnt = (k: 'fp' | 'wm') => rows.filter((r) => (r.kind === 'wm' ? 'wm' : 'fp') === k).length;
  const pendingTotal = rows.reduce((a, r) => a + (r.pending_count || 0), 0);

  /* ── 그룹핑 ── */
  const groupLabelOf = (r: Row): string => {
    if (groupBy === 'company') return (r.company || '').trim() || '소속 없음';
    if (groupBy === 'cohort') return (r.cohort || '').trim() || '기수 없음';
    if (groupBy === 'kind') return r.kind === 'wm' ? 'WM' : 'FP';
    return '';
  };
  const groups = useMemo(() => {
    const m = new Map<string, Row[]>();
    shown.forEach((r) => { const k = groupLabelOf(r); if (!m.has(k)) m.set(k, []); m.get(k)!.push(r); });
    const keys = Array.from(m.keys()).sort((a, b) => {
      const na = a.endsWith(' 없음') ? 1 : 0; const nb = b.endsWith(' 없음') ? 1 : 0; // '없음' 그룹은 마지막
      if (na !== nb) return na - nb;
      if (groupBy === 'cohort') { const x = parseInt(a, 10); const y = parseInt(b, 10); if (!isNaN(x) && !isNaN(y) && x !== y) return x - y; }
      return a.localeCompare(b, 'ko');
    });
    return keys.map((k) => ({ key: k, rows: m.get(k)! }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, groupBy]);
  const setGroup = (g: GroupKey) => { setGroupBy(g); setCollapsed(new Set()); try { localStorage.setItem(GROUP_KEY, g); } catch { /* 무시 */ } };
  const toggleCollapse = (k: string) => setCollapsed((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const sum = (rs: Row[], f: (r: Row) => number) => rs.reduce((a, r) => a + (f(r) || 0), 0);

  /* 기수 빠른 지정 (2026-09-10 지시: 기존 등록된 FP도 기수를 바로 지정해 그룹핑) — 이름 옆 기수 배지(없으면 「+ 기수」) 클릭 → 셀렉트(기존 기수 · 새 기수 입력 · 해제) → 즉시 저장 */
  const [cohortEditId, setCohortEditId] = useState<string | null>(null);
  const [cohortNew, setCohortNew] = useState('');
  const cohortList = useMemo(() => Array.from(new Set(rows.map((r) => (r.cohort || '').trim()).filter(Boolean))).sort((a, b) => { const x = parseInt(a, 10), y = parseInt(b, 10); return (!isNaN(x) && !isNaN(y) && x !== y) ? x - y : a.localeCompare(b, 'ko'); }), [rows]);
  const saveCohort = async (r: Row, cohort: string) => {
    const v = cohort.trim().slice(0, 20);
    setBusy(true);
    try {
      const res = await fetch('/api/leads/fp-portals/' + r.id, { method: 'PUT', headers: H(), body: JSON.stringify({ cohort: v }) });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || '저장 실패');
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, cohort: v } : x)));
      setCohortEditId(null); setCohortNew('');
      onToast(r.name + (v ? ' → ' + v + ' 지정' : ' 기수 해제'));
    } catch (e) { onToast((e as Error).message, 'error'); } finally { setBusy(false); }
  };
  const cohortPicker = (r: Row) => (
    <span className="ld-cohort-pick" onClick={(e) => e.stopPropagation()}>
      <select autoFocus style={{ ...inS, width: 'auto', display: 'inline-block', height: 26, padding: '0 6px', fontSize: 11.5 }} value={cohortNew === '__new' ? '__new' : (r.cohort || '')} disabled={busy}
        onChange={(e) => { const v = e.target.value; if (v === '__new') { setCohortNew('__new'); return; } saveCohort(r, v); }}>
        <option value="">기수 없음</option>
        {cohortList.map((c) => <option key={c} value={c}>{c}</option>)}
        <option value="__new">새 기수 입력…</option>
      </select>
      {cohortNew === '__new' && (
        <input autoFocus style={{ ...inS, width: 74, display: 'inline-block', height: 26, marginLeft: 4, fontSize: 11.5 }} placeholder="예: 3기" disabled={busy}
          onKeyDown={(e) => { if (e.key === 'Enter') saveCohort(r, (e.target as HTMLInputElement).value); if (e.key === 'Escape') { setCohortEditId(null); setCohortNew(''); } }} />
      )}
      <button type="button" className="ce-btn sub" style={{ padding: '3px 7px', fontSize: 10.5, marginLeft: 4, height: 26 }} onClick={() => { setCohortEditId(null); setCohortNew(''); }}>✕</button>
    </span>
  );
  const startEdit = (r: Row) => { setEditId(r.id); setEName(r.name); setECo(r.company); setEPhone(r.phone); setECart(String(r.cart_size || 10)); setECohort(r.cohort || ''); };
  const save = async (r: Row) => {
    if (!eName.trim()) { onToast('이름은 필수입니다', 'error'); return; }
    setBusy(true);
    try {
      const cart = Math.max(0, Math.min(999, parseInt(eCart, 10) || 0));
      const res = await fetch('/api/leads/fp-portals/' + r.id, { method: 'PUT', headers: H(), body: JSON.stringify({ name: eName, company: eCo, phone: ePhone, cart_size: cart, cohort: eCohort }) });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || '저장 실패');
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, name: eName.trim(), company: eCo.trim(), phone: ePhone.trim(), cart_size: cart, cohort: eCohort.trim() } : x)));
      setEditId(null);
      onToast('수정했습니다 — 배정·포털·업무관리 전 모듈에 반영됩니다');
    } catch (e) { onToast((e as Error).message, 'error'); } finally { setBusy(false); }
  };

  /** 미배정 WM — 클릭 시점 동기 창 + 포털 준비 후 이동 (팝업 차단 방지 패턴) */
  const openWm = async (r: Row) => {
    const w = window.open('', '_blank');
    if (!w) { onToast('팝업이 차단되었습니다', 'error'); return; }
    try {
      const res = await fetch('/api/leads/fp-portals/ensure-wm', { method: 'POST', headers: H(), body: JSON.stringify({ wm_id: r.id }) });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || '포털 준비 실패');
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, portal_url: j.data.portal_url } : x)));
      w.location.href = j.data.portal_url;
    } catch (e) { w.close(); onToast((e as Error).message, 'error'); }
  };

  /** 링크 보내기 — ① 미리보기(수신번호·문안·URL·바이트) → ② 확인 후 발송 (2026-09-10) */
  const openPreview = async (r: Row) => {
    setBusy(true);
    try {
      const d = await sendLinkPreview(r.id);
      setPreview({ row: r, data: d, message: d.message, phone: d.to });
    } catch (e) { onToast((e as Error).message, 'error'); } finally { setBusy(false); }
  };
  const doSend = async () => {
    if (!preview) return;
    const digits = preview.phone.replace(/[^0-9]/g, '');
    if (digits.length < 10) { onToast('수신번호를 숫자 10~11자리로 입력하세요', 'error'); return; }
    if (!preview.message.trim()) { onToast('문자 내용을 입력하세요', 'error'); return; }
    setBusy(true);
    try {
      const r = await sendLink(preview.row.id, { message: preview.message.trim(), phone: digits });
      onToast(preview.row.name + ' 님께 링크 문자를 보냈습니다 (' + r.to + ')');
      setPreview(null);
    } catch (e) { onToast((e as Error).message, 'error'); } finally { setBusy(false); }
  };
  const smsBytes = (s: string) => { let n = 0; for (const ch of s) n += ch.charCodeAt(0) > 127 ? 2 : 1; return n; };

  const copy = async (r: Row) => {
    try {
      let url = r.portal_url;
      if (!url) {
        const res = await fetch('/api/leads/fp-portals/ensure-wm', { method: 'POST', headers: H(), body: JSON.stringify({ wm_id: r.id }) });
        const j = await res.json();
        if (!j.success) throw new Error(j.error || '포털 준비 실패');
        url = j.data.portal_url;
        setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, portal_url: url } : x)));
      }
      await navigator.clipboard.writeText(window.location.origin + url);
      onToast(r.name + ' 포털 링크 복사됨 — 카톡에 붙여넣으세요');
    } catch (e) { onToast('클립보드 복사 실패 — 브라우저 권한을 확인하세요', 'error'); }
  };

  const inS: React.CSSProperties = { border: '1px solid #D1D5DB', borderRadius: 7, padding: '6px 9px', fontSize: 12.5, width: '100%', boxSizing: 'border-box' };
  const renderRow = (r: Row) => {
    const editing = editId === r.id;
    const open = heldFor === r.id;
    return (
      <React.Fragment key={r.id}>
      <tr className={open ? 'ld-row-open' : ''}>
        <td>{r.kind === 'wm'
          ? <span style={{ fontSize: 11, fontWeight: 700, color: '#7C3AED', background: '#F5F3FF', padding: '2px 9px', borderRadius: 99 }}>WM</span>
          : <span style={{ fontSize: 11, fontWeight: 700, color: '#2563EB', background: '#EFF6FF', padding: '2px 9px', borderRadius: 99 }}>FP</span>}</td>
        <td style={{ minWidth: 110 }}>{editing ? (<>
          <input style={inS} value={eName} onChange={(e) => setEName(e.target.value)} />
          <input style={{ ...inS, marginTop: 4 }} value={eCohort} onChange={(e) => setECohort(e.target.value)} placeholder="기수 (예: 1기)" />
        </>) : (<>
          <b>{r.name}</b>
          {cohortEditId === r.id ? <span style={{ marginLeft: 6 }}>{cohortPicker(r)}</span>
            : r.cohort ? <button type="button" className="ld-cohort-badge" title="기수 바꾸기" onClick={() => { setCohortEditId(r.id); setCohortNew(''); }}>{r.cohort}</button>
            : <button type="button" className="ld-cohort-badge empty" title="기수 지정 — 기수별 그룹에 묶입니다" onClick={() => { setCohortEditId(r.id); setCohortNew(''); }}>+ 기수</button>}
          {r.stats && (r.stats.won + r.stats.lost + r.stats.returned + r.stats.assigned) > 0 && (
            <div style={{ fontSize: 10.5, color: '#6B7280', marginTop: 3 }}>누적 배정 {r.stats.assigned} · <span style={{ color: '#059669', fontWeight: 700 }}>성공 {r.stats.won}</span> · <span style={{ color: '#B91C1C' }}>실패 {r.stats.lost}</span> · 반납 {r.stats.returned}</div>
          )}
        </>)}</td>
        <td className="mut" style={{ minWidth: 150 }}>{editing ? <input style={inS} value={eCo} onChange={(e) => setECo(e.target.value)} placeholder="소속" /> : (r.company || '-')}</td>
        <td className="mut" style={{ minWidth: 130, fontVariantNumeric: 'tabular-nums' }}>{editing ? <input style={inS} value={ePhone} onChange={(e) => setEPhone(e.target.value)} placeholder="010-0000-0000" inputMode="tel" /> : (r.phone || '-')}</td>
        <td>
          <span style={{ fontWeight: 700 }}>{r.active_count}</span><span className="mut"> / </span>
          {editing
            ? <input style={{ ...inS, width: 54, display: 'inline-block' }} value={eCart} onChange={(e) => setECart(e.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" title="정원(장바구니 한도)" />
            : <span className="mut">{r.cart_size}</span>}
          {!editing && r.cart_size > 0 && r.active_count + (r.pending_count || 0) >= r.cart_size && <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: '#B45309', background: '#FFFBEB', padding: '1px 7px', borderRadius: 99 }} title="보유(수락) + 수락 대기 = 정원 — 추가 배정 불가">가득참</span>}
          {!editing && (r.pending_count || 0) > 0 && <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: '#92400E', background: '#FEF3C7', padding: '1px 7px', borderRadius: 99 }} title="배정했지만 아직 포털에서 수락하지 않은 DB">수락 대기 {r.pending_count}</span>}
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {editing ? (<>
            <button className="ce-btn" style={{ padding: '5px 12px', fontSize: 11.5, marginRight: 5 }} disabled={busy} onClick={() => save(r)}>저장</button>
            <button className="ce-btn sub" style={{ padding: '5px 12px', fontSize: 11.5 }} onClick={() => setEditId(null)}>취소</button>
          </>) : (<>
            {r.portal_url ? (
              <a className="ce-btn" style={{ padding: '5px 12px', fontSize: 11.5, textDecoration: 'none', display: 'inline-block', marginRight: 5 }} href={r.portal_url} target="_blank" rel="noreferrer">포털 열기</a>
            ) : (
              <button className="ce-btn" style={{ padding: '5px 12px', fontSize: 11.5, marginRight: 5 }} onClick={() => openWm(r)}>포털 열기</button>
            )}
            <button className="ce-btn sub" style={{ padding: '5px 12px', fontSize: 11.5, marginRight: 5 }} onClick={() => copy(r)}>링크 복사</button>
            <button className="ce-btn sub" style={{ padding: '5px 12px', fontSize: 11.5, marginRight: 5 }} disabled={busy} onClick={() => openPreview(r)} title="문자 내용을 먼저 보여드리고, 확인 후 알리고로 발송합니다">링크 보내기</button>
            <button className="ce-btn sub" style={{ padding: '5px 12px', fontSize: 11.5, marginRight: 5 }} onClick={() => startEdit(r)}>수정</button>
            {(() => { const held = r.active_count + (r.pending_count || 0); return ( // 보유 DB = 수락한 건 + 수락 대기 (회수 대상 전체)
            <button className={'ce-btn' + (open ? '' : ' sub')} style={{ padding: '5px 12px', fontSize: 11.5, ...(open ? {} : (held > 0 ? { color: '#B91C1C', borderColor: '#FECACA' } : {})) }} disabled={held === 0 && !open}
              title={held > 0 ? '보유 DB 목록(수락 대기 포함) — 건별·선택·전체 회수' : '보유 중인 DB가 없습니다'} onClick={() => toggleHeld(r)}>{open ? '접기' : '보유 DB' + (held ? ' ' + held : '') + ' · 회수'}</button>
            ); })()}
          </>)}
        </td>
      </tr>
      {open && renderHeldPanel(r)}
      </React.Fragment>
    );
  };

  return (
    <div className="ce-card">
      {(() => { const full = rows.filter((r) => r.cart_size > 0 && r.active_count + (r.pending_count || 0) >= r.cart_size); return full.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '9px 14px', marginBottom: 12, fontSize: 12.5, color: '#92400E' }}>
          <b>⚠ 장바구니 가득 {full.length}명</b>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{full.slice(0, 8).map((r) => r.name + ' ' + r.active_count + '/' + r.cart_size).join(' · ')}{full.length > 8 ? ' 외 ' + (full.length - 8) + '명' : ''}</span>
          <span style={{ marginLeft: 'auto', flexShrink: 0, color: '#B45309' }}>추가 배정 불가 — 정원 조정 또는 회수 필요</span>
        </div>
      ); })()}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <input className="ce-inp" style={{ flex: 1, minWidth: 220, maxWidth: 340 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="이름·소속·연락처 검색" />
        <div style={{ display: 'flex', gap: 4 }}>
          {([['all', '전체 ' + rows.length], ['fp', 'FP ' + cnt('fp')], ['wm', 'WM ' + cnt('wm')]] as const).map(([k, label]) => (
            <button key={k} className={'ce-btn' + (kindF === k ? '' : ' sub')} style={{ padding: '6px 13px', fontSize: 12 }} onClick={() => setKindF(k)}>{label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, borderLeft: '1px solid #E5E7EB', paddingLeft: 10 }} title="목록을 묶어 보는 기준">
          <span style={{ fontSize: 11.5, color: '#6B7280', marginRight: 2 }}>그룹</span>
          {([['company', '소속별'], ['cohort', '기수별'], ['kind', 'FP/WM'], ['none', '없음']] as [GroupKey, string][]).map(([k, label]) => (
            <button key={k} className={'ce-btn' + (groupBy === k ? '' : ' sub')} style={{ padding: '6px 11px', fontSize: 12 }} onClick={() => setGroup(k)}>{label}</button>
          ))}
        </div>
        {pendingTotal > 0 && <span style={{ fontSize: 11.5, fontWeight: 700, color: '#92400E', background: '#FEF3C7', padding: '4px 10px', borderRadius: 99 }} title="배정했지만 담당자가 아직 수락하지 않은 DB (전체)">수락 대기 {pendingTotal}건</span>}
        <button className={'ce-btn' + (addOpen ? '' : ' sub')} style={{ padding: '6px 13px', fontSize: 12, marginLeft: 'auto' }} onClick={() => setAddOpen(!addOpen)}>{addOpen ? '등록 닫기' : '+ FP 신규등록'}</button>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {Array.from(new Set(rows.map((r) => (r.cohort || '').trim()).filter(Boolean))).sort().map((c) => (
            <button key={c} className={'ce-btn' + (cohortF === c ? '' : ' sub')} style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => setCohortF(cohortF === c ? '' : c)}>{c}</button>
          ))}
        </div>
      </div>
      {addOpen && (
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.6fr 1.4fr 0.6fr 0.8fr auto', gap: 8, alignItems: 'end', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
          {([['이름 *', nName, setNName, '홍길동', 'text'], ['소속', nCo, setNCo, '한화생명금융서비스', 'text'], ['연락처 *', nPhone, setNPhone, '010-0000-0000', 'tel'], ['정원', nCart, setNCart, '10', 'numeric'], ['기수', nCohort, setNCohort, '예: 3기', 'text']] as const).map(([lab, v, set, ph, im]) => (
            <label key={lab} style={{ fontSize: 11.5, color: '#64748B', fontWeight: 600 }}>{lab}
              <input style={{ ...inS, marginTop: 4 }} value={v} placeholder={ph} inputMode={im} onChange={(e) => set(im === 'numeric' ? e.target.value.replace(/[^0-9]/g, '') : e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addFp(); }} />
            </label>
          ))}
          <button className="ce-btn" style={{ padding: '7px 16px', fontSize: 12.5, height: 34 }} disabled={busy} onClick={addFp}>{busy ? '등록 중…' : '등록'}</button>
          <div style={{ gridColumn: '1 / -1', fontSize: 11.5, color: '#94A3B8' }}>연락처가 같은 담당자가 이미 있으면 등록되지 않고 기존 이름을 알려줍니다. WM은 업무관리 → WM 관리에서 등록하세요.</div>
        </div>
      )}
      <table className="ce-tbl">
        <thead><tr><th>구분</th><th>이름</th><th>소속</th><th>연락처</th><th title="보유 = 담당자가 수락한 진행 건 · 수락 대기는 별도 표시 · 정원 점유는 둘의 합">보유 (수락/정원)</th><th></th></tr></thead>
        <tbody>
          {groupBy === 'none' ? shown.map(renderRow) : groups.map((g) => {
            const off = collapsed.has(g.key);
            const pend = sum(g.rows, (r) => r.pending_count || 0);
            return (
              <React.Fragment key={g.key}>
                <tr style={{ background: '#F8FAFC', cursor: 'pointer' }} onClick={() => toggleCollapse(g.key)} title={off ? '펼치기' : '접기'}>
                  <td colSpan={6} style={{ padding: '8px 10px', borderTop: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB' }}>
                    <span style={{ display: 'inline-block', width: 14, color: '#9CA3AF', fontSize: 11 }}>{off ? '▶' : '▼'}</span>
                    <b style={{ fontSize: 12.5 }}>{g.key}</b>
                    <span style={{ marginLeft: 8, fontSize: 11.5, color: '#6B7280' }}>{g.rows.length}명 · 보유 {sum(g.rows, (r) => r.active_count)} / 정원 {sum(g.rows, (r) => r.cart_size)}
                      {pend > 0 && <span style={{ marginLeft: 6, color: '#92400E', fontWeight: 700 }}>· 수락 대기 {pend}</span>}
                      {g.rows.some((r) => r.stats) && <span> · 성공 {sum(g.rows, (r) => (r.stats ? r.stats.won : 0))}</span>}
                    </span>
                  </td>
                </tr>
                {!off && g.rows.map(renderRow)}
              </React.Fragment>
            );
          })}
          {shown.length === 0 && <tr><td colSpan={6} className="ce-none">{loading ? '불러오는 중…' : (t ? '검색 결과가 없습니다' : '등록된 대상이 없습니다 — 배정 시 자동 등록됩니다')}</td></tr>}
        </tbody>
      </table>

      {preview && (
        <div className="ld-modal-bg" onClick={() => !busy && setPreview(null)}>
          <div className="ld-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="링크 보내기 미리보기">
            <div className="ld-modal-h"><span>링크 보내기 — 이렇게 발송됩니다</span><button className="ld-btn" style={{ padding: '4px 10px' }} onClick={() => setPreview(null)}>닫기</button></div>
            <div className="ld-modal-b">
              <div style={{ display: 'grid', gridTemplateColumns: '92px 1fr', rowGap: 8, columnGap: 10, fontSize: 13, alignItems: 'center' }}>
                <span style={{ color: '#6B7280' }}>받는 사람</span>
                <span><b>{preview.row.name}</b> {preview.data.kind === 'wm' ? 'WM' : 'FP'}{preview.data.company ? <span style={{ color: '#6B7280' }}> · {preview.data.company}</span> : null}</span>
                <span style={{ color: '#6B7280' }}>수신번호</span>
                <input style={{ ...inS, maxWidth: 220, fontVariantNumeric: 'tabular-nums' }} value={preview.phone} inputMode="tel" onChange={(e) => setPreview({ ...preview, phone: e.target.value })} placeholder="010-0000-0000" />
                <span style={{ color: '#6B7280' }}>포털 링크</span>
                <span style={{ fontSize: 12.5, wordBreak: 'break-all' }}><a href={preview.data.full_url} target="_blank" rel="noreferrer">{preview.data.url}</a>{preview.data.url !== preview.data.full_url && <span style={{ color: '#9CA3AF' }}> (단축 URL)</span>}</span>
                <span style={{ color: '#6B7280', alignSelf: 'start', paddingTop: 6 }}>문자 내용</span>
                <div>
                  <textarea style={{ ...inS, minHeight: 118, resize: 'vertical', lineHeight: 1.55, fontFamily: 'inherit' }} value={preview.message} onChange={(e) => setPreview({ ...preview, message: e.target.value })} />
                  {(() => { const b = smsBytes(preview.message); return (
                    <div style={{ fontSize: 11.5, color: b > 90 ? '#B45309' : '#6B7280', marginTop: 4 }}>{b} byte · {b > 90 ? 'LMS(장문)로 발송됩니다' : 'SMS(단문)로 발송됩니다'} · 링크는 본문에 그대로 포함되어야 합니다</div>
                  ); })()}
                </div>
              </div>
              <div style={{ marginTop: 12, padding: '9px 12px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
                미리보기 문안은 실제 발송 문안과 같습니다. 내용을 고쳐 보내면 고친 대로 발송되며, 발송 내역은 문자 발송 기록(sms_logs)에 남습니다.
              </div>
            </div>
            <div className="ld-modal-f">
              <button className="ld-btn" onClick={() => setPreview(null)} disabled={busy}>취소</button>
              <button className="ld-btn pri" onClick={doSend} disabled={busy}>{busy ? '발송 중…' : '이대로 보내기'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
